/**
 * Main menu page component
 */
import { useState, useEffect, useCallback } from 'react'
import { AlertCircle } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui'
import { RestaurantHeader } from '@/components/layout/RestaurantHeader'
import { Footer } from '@/components/layout/Footer'
import { ItemCustomizationModal } from '@/components/customization'
import { CartSidebar, FloatingCartButton } from '@/components/cart'
import { CheckoutFAQ } from '@/components/checkout'
import {
  CategoryNav,
  MenuSection,
  MenuLoadingSkeleton,
  FeaturedCarousel,
} from '@/components/menu'
import { useTransformedMenuData } from '@/hooks/useTransformedMenuData'
import { MenuItem } from '@/types/menu'

interface MenuPageProps {
  onNavigate: (path: string) => void
}

export function MenuPage({ onNavigate }: MenuPageProps) {
  const { collections, itemsByCollection, featuredItems, isLoading, isError, error } =
    useTransformedMenuData()

  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [activeSection, setActiveSection] = useState<string | null>(null)

  // Set first section active on load
  useEffect(() => {
    if (collections.length > 0 && !activeSection) {
      setActiveSection(collections[0].name)
    }
  }, [collections, activeSection])

  // Observe visible menu sections to sync active nav item
  useEffect(() => {
    if (!collections.length) return

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the topmost intersecting section
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]

        if (visible) {
          const sectionId = visible.target.getAttribute('id')?.replace('section-', '')
          const section = collections.find(c => c.id === sectionId)
          if (section) {
            setActiveSection(section.name)
          }
        }
      },
      {
        root: null,
        rootMargin: '-120px 0px -65% 0px',
        threshold: [0, 0.25, 0.5]
      }
    )

    collections.forEach((collection) => {
      const el = document.getElementById(`section-${collection.id}`)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [collections])

  const handleSectionClick = (sectionName: string) => {
    const section = collections.find(c => c.name === sectionName)
    if (!section) return

    const el = document.getElementById(`section-${section.id}`)
    if (el) {
      // Account for sticky header height (CategoryNav is ~60px on mobile, ~70px on desktop)
      const offset = 120
      const y = el.getBoundingClientRect().top + window.scrollY - offset
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }

  const handleItemClick = useCallback((item: MenuItem) => {
    setSelectedItem(item)
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setSelectedItem(null)
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 flex flex-col">
      <div className="flex-1">
        <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-7xl pb-24">
          <RestaurantHeader />

          {/* Error State */}
          {isError && (
            <Alert variant="destructive" className="mb-4 sm:mb-6 animate-scale-in">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error loading menu</AlertTitle>
              <AlertDescription>
                {(error as { data?: { message?: string } })?.data?.message || 'Failed to load menu. Please try again later.'}
              </AlertDescription>
            </Alert>
          )}

          {/* Loading State */}
          {isLoading && <MenuLoadingSkeleton />}

          {/* Featured Items Carousel */}
          {!isLoading && featuredItems.length > 0 && (
            <FeaturedCarousel
              items={featuredItems}
              onItemClick={handleItemClick}
            />
          )}

          {/* Section Navigation */}
          {!isLoading && collections.length > 0 && (
            <CategoryNav
              categories={collections.map(c => ({ id: c.id, name: c.name }))}
              activeCategory={activeSection}
              onCategoryClick={handleSectionClick}
            />
          )}

          {/* Menu Sections */}
          {!isLoading && collections.length > 0 && (
            <div className="space-y-8 sm:space-y-10 animate-fade-in">
              {collections.map((collection) => (
                <MenuSection
                  key={collection.id}
                  id={collection.id}
                  name={collection.name}
                  description={collection.description}
                  items={itemsByCollection[collection.id] || []}
                  onItemClick={handleItemClick}
                />
              ))}
            </div>
          )}

          {/* FAQ */}
          {!isLoading && <CheckoutFAQ />}
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Customization Modal */}
      <ItemCustomizationModal
        item={selectedItem}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />

      {/* Cart Sidebar */}
      <CartSidebar onProceedToCheckout={() => onNavigate('/checkout')} />

      {/* Floating Cart Button */}
      <FloatingCartButton />
    </div>
  )
}
